import { z } from 'zod';
// ---------- A2 Importación de clientes y contactos desde Excel ----------
/**
 * Qué hacer con un cliente del fichero que ya existe en Toga (mismo NIF):
 *  - `skip`: no tocarlo (y no añadirle los contactos de la hoja Contactos).
 *  - `fill`: rellenar SOLO sus campos vacíos y añadirle los contactos que falten.
 * Nunca se sobrescribe un dato que ya esté escrito.
 */
export const ImportModeSchema = z.enum(['skip', 'fill']);
export const ImportIssueSchema = z.object({
    sheet: z.enum(['file', 'clients', 'contacts']),
    /** Fila del libro (1 = la primera). 0 para avisos que afectan al fichero entero. */
    row: z.number().int(),
    level: z.enum(['error', 'warning']),
    message: z.string(),
});
export const ImportSheetMapSchema = z.object({
    sheet: z.string(),
    /** Fila del libro donde se han encontrado las cabeceras. */
    headerRow: z.number().int(),
    mapped: z.array(z.object({ header: z.string(), label: z.string() })),
    /** Cabeceras que no se reconocen y se ignoran. */
    ignored: z.array(z.string()),
});
export const ImportRowStatusSchema = z.enum(['new', 'existing', 'duplicate', 'invalid']);
export const ImportSampleRowSchema = z.object({
    row: z.number().int(),
    status: ImportRowStatusSchema,
    displayName: z.string(),
    nationalId: z.string().nullable(),
    email: z.string().nullable(),
    /** Campos que se rellenarían (solo en un cliente que ya existe). */
    fills: z.array(z.string()),
});
export const ImportTotalsSchema = z.object({
    clientRows: z.number().int(),
    clientsNew: z.number().int(),
    clientsExisting: z.number().int(),
    /** De los que ya existen, cuántos tienen algún campo vacío que el fichero podría rellenar. */
    clientsFillable: z.number().int(),
    clientsDuplicated: z.number().int(),
    clientsInvalid: z.number().int(),
    contactRows: z.number().int(),
    contactsNew: z.number().int(),
    contactsSkipped: z.number().int(),
    contactsInvalid: z.number().int(),
});
export const ClientImportPreviewSchema = z.object({
    fileName: z.string(),
    mode: ImportModeSchema,
    clientsSheet: ImportSheetMapSchema.nullable(),
    contactsSheet: ImportSheetMapSchema.nullable(),
    totals: ImportTotalsSchema,
    /** Primeras filas de clientes, para que se vea cómo se ha entendido el fichero. */
    sample: z.array(ImportSampleRowSchema),
    issues: z.array(ImportIssueSchema),
    /** Nº total de incidencias (`issues` se recorta). */
    issuesTotal: z.number().int(),
});
export const ClientImportBatchDtoSchema = z.object({
    id: z.string(),
    fileName: z.string(),
    origin: z.string(),
    mode: ImportModeSchema,
    rowsClients: z.number().int(),
    rowsContacts: z.number().int(),
    clientsCreated: z.number().int(),
    clientsFilled: z.number().int(),
    clientsSkipped: z.number().int(),
    contactsCreated: z.number().int(),
    createdAt: z.string(),
    createdByName: z.string().nullable(),
    undoneAt: z.string().nullable(),
});
export const ClientImportResultSchema = z.object({
    batch: ClientImportBatchDtoSchema,
    issues: z.array(ImportIssueSchema),
    issuesTotal: z.number().int(),
});
export const ClientImportBatchListResponseSchema = z.object({
    items: z.array(ClientImportBatchDtoSchema),
});
/** Declaración de origen obligatoria al confirmar (base jurídica de la lista). */
export const IMPORT_ORIGIN_MIN_LENGTH = 10;
export const IMPORT_ORIGIN_MAX_LENGTH = 500;
export const ImportOriginSchema = z
    .string()
    .trim()
    .min(IMPORT_ORIGIN_MIN_LENGTH, 'Indica de dónde proceden estos datos (mín. 10 caracteres)')
    .max(IMPORT_ORIGIN_MAX_LENGTH);
//# sourceMappingURL=client-import.js.map